import {HttpClient, HttpParams} from "@angular/common/http";
import {inject, signal, WritableSignal} from "@angular/core";
import {isArray} from "lodash";
import {environment} from "../../../environments/environment";
import {LoaderModel} from "../models/loader.model";
import {PaginatorModel} from "../models/paginator.model";
import {GlobalListResponseModel} from '@core/models/global-list-response.model';
import {GlobalResponseModel} from '@core/models/global-response.model';

export class WithApi<T> {
  public readonly isLoading: WritableSignal<LoaderModel> = signal({
    delete: 'Idle',
    getAll: 'Idle',
    save: 'Idle',
    show: 'Idle',
    update: 'Idle'
  })
  public readonly paginator: WritableSignal<PaginatorModel | null> = signal(null)
  public readonly items: WritableSignal<T[]> = signal([])
  public readonly currentItem: WritableSignal<T | null> = signal(null)
  public readonly error = signal('')
  public url = environment.baseUrl
  public http = inject(HttpClient)

  constructor(urlE: string) {

    this.url += urlE
  }

  getAll(params?: HttpParams, callBack?: Function, withDataAccessor: boolean = true) {
    this.isLoading.update(loader => ({
      ...loader,
      getAll: 'Loading'
    }))
    this.http.get<GlobalListResponseModel<T>>(this.url, {
      params: params
    }).subscribe({
      next: (response: any) => {
        this.isLoading.update(loader => ({
          ...loader,
          getAll: 'Success'
        }))
        if (withDataAccessor) {
          if (isArray(response.data)) {
            this.items.set(response.data)
          } else {
            if (isArray(response.data.items)) {

              this.items.set(response.data.items)
              if (response.data.pagination) {
                this.paginator.set({
                  pageSize: response.data.pagination.per_page,
                  page: response.data.pagination.current_page,
                  first: response.data.pagination.from,
                  last: response.data.pagination.to,
                  total: response.data.pagination.total,
                  totalPages: response.data.pagination.last_page
                })
              }
            }
            if (isArray(response.data.content)) {
              console.log(response.data.items, 'response.data.items')
              this.items.set(response.data.content)
            }
          }
        } else {
          this.items.set(response)
        }
        if (response?.current_page) {
          let page = +(response?.current_page || 0) as number
          let size = +(response?.per_page || 0) as number
          this.paginator.set({
            pageSize: size,
            page: page,
            totalPages: response.last_page,
            total: response.total,
            first: 0,
            last: response.last_page
          })
        }
        if (callBack) {
          callBack(response)
        }
      },
      error: err => {
        this.error.set(err)
        this.isLoading.update(loader => ({
          ...loader,
          getAll: 'Failed'
        }))
      },
      complete: () => {
        setTimeout(() => {
          this.isLoading.update(loader => ({
            ...loader,
            getAll: 'Idle'
          }))
        }, 200)
      }
    })
  }

  findOne(id: string, params?: HttpParams, callBack?: Function, withDataAccessor: boolean = true) {
    this.isLoading.update(loader => ({
      ...loader,
      show: 'Loading'
    }))
    this.http.get<GlobalResponseModel<T>>(this.url + '/' + id, {
      params: params
    }).subscribe({
      next: (response: any) => {
        this.isLoading.update(loader => ({
          ...loader,
          show: 'Success'
        }))
        this.currentItem.set(withDataAccessor ? response.data : response)
        if (callBack) {
          callBack(response)
        }
      },
      error: err => {
        this.error.set(err)
        this.isLoading.update(loader => ({
          ...loader,
          show: 'Failed'
        }))
      },
      complete: () => {
        setTimeout(() => {
          this.isLoading.update(loader => ({
            ...loader,
            show: 'Idle'
          }))
        }, 200)
      }
    })
  }

  create(data: Partial<T>, callBack: Function) {
    this.isLoading.update(loader => ({
      ...loader,
      save: 'Loading'
    }))
    this.http.post<GlobalResponseModel<T>>(this.url, data).subscribe({
      next: (response: any) => {
        this.isLoading.update(loader => ({
          ...loader,
          save: 'Success'
        }))
        this.currentItem.set(response)
        if (callBack) {
          callBack(response)
        }
      },
      error: err => {
        this.error.set(err)
        this.isLoading.update(loader => ({
          ...loader,
          save: 'Failed'
        }))
      },
      complete: () => {
        setTimeout(() => {
          this.isLoading.update(loader => ({
            ...loader,
            save: 'Idle'
          }))
        }, 200)
      }
    })
  }

  update(id: string, data: Partial<T>, params?: HttpParams, callBack?: Function) {
    this.isLoading.update(loader => ({
      ...loader,
      update: 'Loading'
    }))
    this.http.patch<GlobalResponseModel<T>>(this.url + '/' + id, data, {
      params: params,
    }).subscribe({
      next: (response: any) => {
        this.isLoading.update(loader => ({
          ...loader,
          update: 'Success'
        }))
        this.currentItem.set(response.data)
        if (callBack) {
          callBack(response)
        }
      },
      error: err => {
        this.error.set(err)
        this.isLoading.update(loader => ({
          ...loader,
          update: 'Failed'
        }))
      },
      complete: () => {
        setTimeout(() => {
          this.isLoading.update(loader => ({
            ...loader,
            update: 'Idle'
          }))
        }, 200)
      }
    })
  }

  delete(id: string, callBack?: Function) {
    this.isLoading.update(loader => ({
      ...loader,
      update: 'Loading'
    }))
    this.http.delete<GlobalResponseModel<T>>(this.url + '/' + id).subscribe({
      next: (response: any) => {
        this.isLoading.update(loader => ({
          ...loader,
          update: 'Success'
        }))
        this.currentItem.set(null)
        this.items.update(data => {
          return data.filter(item => (item as any)?.id !== id)
        })
        this.currentItem.set(response.data)
        if (callBack) {
          callBack()
        }
      },
      error: err => {
        this.error.set(err)
        console.log(err.error, 'Error')
        // this.toastr.error(err?.error?.reason || err?.error?.message , 'Error')
        this.isLoading.update(loader => ({
          ...loader,
          update: 'Failed'
        }))
      },
      complete: () => {
        setTimeout(() => {
          this.isLoading.update(loader => ({
            ...loader,
            update: 'Idle'
          }))
        }, 200)
      }
    })
  }
}
